import {
  EVERYWHERE,
  SEARCH_PLACE_TYPES,
} from '@web-platform/shared-universal-constants/src/places';
import { MULTI_DESTINATION } from '@web-platform/shared-universal-constants/src/tripTypes';
import { USER_SEARCH_VERSION } from '@web-platform/shared-universal-constants/src/userSearch';
import { makeEntityId, type Nullable } from '@web-platform/shared-universal-types-utils/src/utils';
import { fpsCabinClassToCabinClass } from '@web-platform/shared-universal-utils-frontend/src/helpers/cabinClass';
import { getPlaceType } from '@web-platform/shared-universal-utils-frontend/src/helpers/getPlaceType';
import { isEmptyObject } from '@web-platform/shared-universal-utils-frontend/src/helpers/helpers';
import { isAcornType } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { isPastDay } from '@web-platform/shared-universal-utils-frontend-localisation';

import { translate } from '../../translate';
import { convertFromDashedTripType } from '../tripType';

import { handler } from './handler';

import type { TripType } from '@web-platform/shared-universal-constants/src/tripTypes';
import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { RawSearchParams as BananaRawSearchParams } from '@web-platform/shared-universal-types/src/documentState';
import type { RawSearchParams as AcornRawSearchParams } from '@web-platform/shared-universal-types/src/documentStateAcorn';
import type {
  SearchLeg,
  SearchParams,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';

// Acorn-specific legs function
function getAcornLegs(state: AcornRawSearchParams): SearchLeg[] {
  switch (state.tripType) {
    case 'return':
    case 'one-way':
    case 'multi-destination':
      return state.legs.map((leg) => ({
        origin: {
          id: leg.originId,
          name: leg.originName,
          entityId: makeEntityId(leg.originEntityId),
          cityId: leg.originCityId,
          cityName: leg.originCityName,
          countryId: leg.originCountryId,
          geoContainerId: makeEntityId(leg.originGeoContainerId),
          type: leg.originType,
        },
        destination: {
          id: leg.destinationId,
          name: leg.destinationName,
          entityId: makeEntityId(leg.destinationEntityId),
          cityId: leg.destinationCityId,
          cityName: leg.destinationCityName,
          countryId: leg.destinationCountryId,
          geoContainerId: makeEntityId(leg.destinationGeoContainerId),
          type: leg.destinationType,
        },
        date: leg.date,
      }));

    default:
      throw new Error('Unrecognised trip type');
  }
}

// Banana-specific helper for legs data
function sanitiseOutboundDate(date: DateString | undefined): Nullable<DateString> {
  // ST-4589 We vary between setting an unset date to null or an empty string, pick one pattern, update types, and use consistently
  const defaultValue = '';
  if (!date) return defaultValue;
  return isPastDay(date) ? defaultValue : date;
}

// Banana-specific legs function
function getBananaLegs(rawSearchParam: BananaRawSearchParams): SearchLeg[] {
  const { destination, inboundDate, origin, outboundDate, tripType } = rawSearchParam;

  if (destination && !isEmptyObject(destination)) {
    destination.type = getPlaceType(destination);

    // Everywhere
    if (destination.type === SEARCH_PLACE_TYPES.EVERYWHERE) {
      destination.id = EVERYWHERE;
      destination.name = translate('ktxt_everywhere');
    }
  }

  if (origin && !isEmptyObject(origin)) {
    origin.type = getPlaceType(origin);
  }

  // Multi Destination
  if (tripType === 'multi-destination') {
    // SEARCHEXP-644 This || was introduced as a hotfix, but should be fixed properly and the type adjusted
    const rawLegs = rawSearchParam.legs || rawSearchParam.flightLegs || [];
    if (rawLegs.length > 0) {
      // @ts-expect-error ST-4589 Update SearchState types to reflect the fact we're setting `null` date here.
      return rawLegs.map((leg) => ({
        origin: {
          id: leg.originId,
          name: leg.originName,
          entityId: makeEntityId(leg.originEntityId),
          cityId: leg.originCityId,
          cityName: leg.originCityName,
          countryId: leg.originCountryId,
          geoContainerId: makeEntityId(leg.originGeoContainerId),
          type: leg.originType,
        },
        destination: {
          id: leg.destinationId,
          name: leg.destinationName,
          entityId: makeEntityId(leg.destinationEntityId),
          cityId: leg.destinationCityId,
          cityName: leg.destinationCityName,
          countryId: leg.destinationCountryId,
          geoContainerId: makeEntityId(leg.destinationGeoContainerId),
          type: leg.destinationType,
        },
        date: sanitiseOutboundDate(leg.date),
        return_date: null,
      }));
    }
  }

  // One Way or Return or Multi-destination without legs
  const dateCollection: {
    date: Nullable<DateString>;
    return_date: Nullable<DateString>;
    is_anytime: boolean;
  } = {
    date: '',
    return_date: '',
    is_anytime: false,
  };

  if (tripType === 'one-way' || tripType === 'return') {
    const {
      inboundDate: _inboundDate,
      isAnytime: _isAnytime,
      outboundDate: _outboundDate,
    } = handler({
      tripType,
      outboundDate,
      inboundDate,
    });

    dateCollection.date = _outboundDate;
    dateCollection.return_date = _inboundDate;
    dateCollection.is_anytime = !!_isAnytime;
  } else {
    dateCollection.date = sanitiseOutboundDate(outboundDate);
    dateCollection.return_date = null;
  }

  return [
    {
      origin: { ...origin },
      // @ts-expect-error Destination can now be undefined and will be updated in ST-4470
      destination: destination ? { ...destination } : null,
      // @ts-expect-error ST-4589 Update SearchState types to reflect the fact we're setting `null` date here.
      date: dateCollection.date,
      return_date: dateCollection.return_date,
      is_anytime: dateCollection.is_anytime,
    },
  ];
}

// Banana-specific tripType calculation
function getBananaTripType(rawSearchParam: BananaRawSearchParams): TripType {
  return rawSearchParam.tripType === 'multi-destination'
    ? MULTI_DESTINATION
    : convertFromDashedTripType(
        handler({
          tripType: rawSearchParam.tripType,
          outboundDate: rawSearchParam.outboundDate,
          inboundDate: rawSearchParam.inboundDate,
        }).tripType,
      );
}

export function transformContextUserSearch(
  rawSearchParam: AcornRawSearchParams | BananaRawSearchParams,
): SearchParams {
  if (!rawSearchParam) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {} as SearchParams;
  }

  const isAcorn = isAcornType<AcornRawSearchParams, BananaRawSearchParams>(rawSearchParam);

  const cabinClass = rawSearchParam.cabinClass ? rawSearchParam.cabinClass.toLowerCase() : null;
  const sponsoredAgent =
    'sponsoredAgent' in rawSearchParam ? rawSearchParam.sponsoredAgent : undefined;

  const legs = isAcorn ? getAcornLegs(rawSearchParam) : getBananaLegs(rawSearchParam);
  const tripType = isAcorn
    ? convertFromDashedTripType(rawSearchParam.tripType)
    : getBananaTripType(rawSearchParam);

  return {
    version: USER_SEARCH_VERSION,
    adults: rawSearchParam.adultsV2 || 1,
    alternativeDestinations: !!rawSearchParam.inboundAlts,
    alternativeOrigins: !!rawSearchParam.outboundAlts,
    // fpsToCabinClassMapper defaults to ECONOMY if no cabinClass
    cabinClass: fpsCabinClassToCabinClass(cabinClass),
    child_ages: rawSearchParam.childrenV2 || [],
    inboundDate: rawSearchParam.inboundDate,
    outboundDate: rawSearchParam.outboundDate,
    legs,
    options: {
      include_unpriced_itineraries: true,
      include_mixed_booking_options: true,
    },
    prefer_directs: rawSearchParam.preferDirects,
    tripType,
    fareAttributes: rawSearchParam.fareAttributes,
    sponsoredAgent,
  };
}
